// src/utils/formatters.js
export const formatters = {
  // Форматирование даты
  formatDate: (dateString, format = 'short') => {
    if (!dateString) return '—';
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Неверная дата';
    
    const options = {
      short: { day: '2-digit', month: '2-digit', year: 'numeric' },
      long: { day: 'numeric', month: 'long', year: 'numeric' },
      datetime: { 
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      },
      time: { hour: '2-digit', minute: '2-digit' },
      timeago: null // специальный формат
    };
    
    if (format === 'timeago') {
      return this.formatTimeAgo(date);
    }
    
    return date.toLocaleDateString('ru-RU', options[format] || options.short);
  },

  // Относительное время
  formatTimeAgo: (date) => {
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'только что';
    if (diffMins < 60) return `${diffMins} мин назад`;
    if (diffHours < 24) return `${diffHours} ч назад`;
    if (diffDays < 7) return `${diffDays} дн назад`;
    return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
  },

  // Форматирование имени пользователя
  getUserDisplayName: (user) => {
    if (!user) return 'Неизвестно';
    
    if (user.error) {
      return user.name || `Пользователь #${user.id}`;
    }
    
    const firstName = user.telegram_first_name || user.first_name || '';
    const lastName = user.telegram_last_name || user.last_name || '';
    const userName = user.username || user.name || '';
    
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (lastName) {
      return lastName;
    } else if (userName) {
      return userName;
    }
    
    return `Пользователь #${user.id}`;
  },

  // Инициалы пользователя
  getUserInitials: (user) => {
    if (!user) return 'U';
    
    const firstName = user.telegram_first_name || user.first_name || '';
    const lastName = user.telegram_last_name || user.last_name || '';
    const userName = user.username || user.name || '';
    
    if (firstName || lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    
    if (userName) {
      return userName
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .substring(0, 2);
    }
    
    return 'U';
  },

  // Форматирование статуса задачи
  formatTaskStatus: (status) => {
    const statusMap = {
      'open': 'Открыта',
      'assigned': 'Назначена',
      'in_progress': 'В работе',
      'completed': 'Выполнена',
      'cancelled': 'Отменена',
      'reopened': 'Переоткрыта'
    };
    return statusMap[status] || status;
  },

  // Получение варианта бейджа для статуса
  getStatusBadgeVariant: (status) => {
    const variants = {
      'open': 'secondary',
      'assigned': 'info',
      'in_progress': 'warning',
      'completed': 'success',
      'cancelled': 'danger',
      'reopened': 'primary'
    };
    return variants[status] || 'secondary';
  },

  // Форматирование приоритета задачи
  formatTaskPriority: (priority) => {
    const priorityMap = {
      'low': 'Низкий',
      'medium': 'Средний',
      'high': 'Высокий',
      'urgent': 'Срочный'
    };
    return priorityMap[priority] || priority;
  },

  // Получение варианта бейджа для приоритета
  getPriorityBadgeVariant: (priority) => {
    const variants = {
      'low': 'success',
      'medium': 'info',
      'high': 'warning',
      'urgent': 'danger'
    };
    return variants[priority] || 'secondary';
  },

  // Форматирование категории задачи
  formatTaskCategory: (category) => {
    const categoryMap = {
      'technical': 'Техническая',
      'administrative': 'Административная',
      'feature_request': 'Запрос функционала',
      'bug': 'Ошибка',
      'other': 'Другое'
    };
    return categoryMap[category] || category;
  },

  // Форматирование размера файла
  formatFileSize: (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  },

  // Получение иконки для типа файла
  getFileIcon: (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    const icons = {
      pdf: 'bi-file-pdf',
      doc: 'bi-file-word',
      docx: 'bi-file-word',
      xls: 'bi-file-excel',
      xlsx: 'bi-file-excel',
      ppt: 'bi-file-ppt',
      pptx: 'bi-file-ppt',
      jpg: 'bi-file-image',
      jpeg: 'bi-file-image',
      png: 'bi-file-image',
      gif: 'bi-file-image',
      txt: 'bi-file-text',
      zip: 'bi-file-zip',
      rar: 'bi-file-zip',
      '7z': 'bi-file-zip'
    };
    return icons[ext] || 'bi-file-earmark';
  },

  // Получение цвета иконки файла
  getFileIconColor: (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    const colors = {
      pdf: 'text-danger',
      doc: 'text-primary',
      docx: 'text-primary',
      xls: 'text-success',
      xlsx: 'text-success',
      ppt: 'text-warning',
      pptx: 'text-warning',
      jpg: 'text-info',
      jpeg: 'text-info',
      png: 'text-info',
      gif: 'text-info',
      txt: 'text-secondary',
      zip: 'text-dark',
      rar: 'text-dark',
      '7z': 'text-dark'
    };
    return colors[ext] || 'text-primary';
  },

  // Форматирование роли пользователя
  formatUserRole: (role) => {
    const roleMap = {
      'admin': 'Администратор',
      'moderator': 'Модератор',
      'user': 'Пользователь'
    };
    return roleMap[role] || role;
  },

  // Получение варианта бейджа для роли
  getRoleBadgeVariant: (role) => {
    const variants = {
      'admin': 'danger',
      'moderator': 'warning',
      'user': 'secondary'
    };
    return variants[role] || 'secondary';
  },

  // Форматирование типа уведомления
  formatNotificationType: (type) => {
    const typeMap = {
      'info': 'Информация',
      'success': 'Успех',
      'warning': 'Предупреждение',
      'error': 'Ошибка',
      'system': 'Системное',
      'task': 'Задача',
      'chat': 'Чат'
    };
    return typeMap[type] || type;
  },

  // Получение варианта бейджа для типа уведомления
  getNotificationTypeVariant: (type) => {
    const variants = {
      'success': 'success',
      'warning': 'warning',
      'error': 'danger',
      'system': 'primary',
      'task': 'info',
      'chat': 'info',
      'info': 'info'
    };
    return variants[type] || 'info';
  },

  // Получение иконки для типа уведомления
  getNotificationIcon: (type) => {
    const icons = {
      'success': 'bi-check-circle-fill',
      'warning': 'bi-exclamation-triangle-fill',
      'error': 'bi-x-circle-fill',
      'system': 'bi-gear-fill',
      'task': 'bi-list-task',
      'chat': 'bi-chat-fill',
      'info': 'bi-info-circle-fill'
    };
    return icons[type] || 'bi-info-circle-fill';
  }
};